// Copyright ©️ 2026 eVolve MEP, LLC

/// <reference types="forge-viewer" />
import './forge-viewer.css';

import './extensions/SelectionToolsExtension/SelectionToolsExtension';

import { useCallback, useRef } from 'react';

import { useDebouncedCallback } from '@mantine/hooks';

import { EvolveWebSpoolView } from 'components/Autodesk/constants';
import { DeferredForgeViewer } from 'components/Autodesk/DeferredForgeViewer';
import { type BuiltInExtension, useHideExtensions } from 'components/Autodesk/extensions/useHideExtensions';
import { findDocumentNode } from 'components/Autodesk/extensions/utils';
import { useViewer } from 'components/Autodesk/hooks/useViewer';
import { useViewerPortals } from 'components/Autodesk/hooks/useViewerPortals';
import type { AutodeskExtensionBuiltIn } from 'components/Autodesk/types';
import { isNil, isNotNil } from 'helpers/isNotNil';

import { useAutodeskAuth } from './auth/useAutodeskAuth';
import { useEvolveExtensions } from './useEvolveExtensions';

const { Autodesk } = window;

export type ForgeViewerProps = Autodesk.Viewing.InitializerOptions & {
  /** Plain (non-encoded) urn with version */
  urn: string;
  /** If provided, will attempt to isolate this Spool after the model is loaded */
  autoSelectSpool?: string;
  /**
   * If `true` and spools exist in the current view, will show the Spool menu.
   * @default true
   * */
  showSpoolMenu?: boolean;
  /** Hide these extensions from the toolbar */
  hideExtensions?: BuiltInExtension[];
};

// These are always loaded.
const defaultExtensions = [
  'Autodesk.PropertiesManager',
  'Autodesk.DocumentBrowser',
  'Autodesk.Measure',
  'Autodesk.ViewerSettings',
  'Autodesk.ModelStructure',
  'Autodesk.DefaultTools.NavTools',
  'Autodesk.ViewCubeUi',
  'Autodesk.Section',
] as const satisfies AutodeskExtensionBuiltIn[];

const ForgeViewerWrapped = ({
  urn,
  autoSelectSpool,
  showSpoolMenu,
  hideExtensions: extensionsToHide = [],
  ...props
}: ForgeViewerProps) => {
  const { getAccessTokenCb } = useAutodeskAuth();
  const { mounted, viewer, viewerRuntimePromise } = useViewer();
  const hideExtensions = useHideExtensions(extensionsToHide);

  const { mountPortal, portals } = useViewerPortals();
  const { contexts, loadEvolveExtension } = useEvolveExtensions();

  const viewerHasAuth = useRef(false);
  const initializeViewerRuntime = useCallback(
    async (rtProps: Autodesk.Viewing.InitializerOptions) => {
      const { getAccessToken, env = 'AutodeskProduction2', api = 'streamingV2', shouldInitializeAuth = true } = rtProps;
      if (!viewerRuntimePromise.current) {
        viewerHasAuth.current = shouldInitializeAuth;
        // oxlint-disable-next-line react/immutability
        viewerRuntimePromise.current = new Promise<void>((resolve) =>
          Autodesk.Viewing.Initializer({ getAccessToken, env, api, shouldInitializeAuth }, resolve),
        );
      }

      const runtime = await viewerRuntimePromise.current;
      if (shouldInitializeAuth !== viewerHasAuth.current) {
        viewerRuntimePromise.current = null;
        // oxlint-disable-next-line react/immutability
        return initializeViewerRuntime(rtProps);
      }
      return runtime;
    },
    [viewerHasAuth, viewerRuntimePromise],
  );

  const syncState = useCallback(
    async (prevProps: Omit<ForgeViewerProps, 'urn'>) => {
      if (!urn || urn === prevProps.urn || viewer.current === undefined) return;

      // Load translated model from ACC/BIM360
      Autodesk.Viewing.Document.load(
        `urn:${btoa(urn).replaceAll('=', '').replaceAll('/', '_')}`,
        (doc) => {
          const evolveSpoolView = findDocumentNode(
            doc.getRoot().getRootNode(),
            (n) =>
              // Even though `n.data.name` is typed as non-nil, it can still sometimes be nil.
              isNotNil(n.data.name) &&
              n.data.name.toLowerCase().trim() === EvolveWebSpoolView.toLowerCase() &&
              n.data.type === 'geometry',
          );
          const document = evolveSpoolView?.getDocument() ?? doc;
          const geometry = evolveSpoolView?.getDefaultGeometry() ?? doc.getRoot().getDefaultGeometry();
          return viewer.current?.loadDocumentNode(document, geometry);
        },
        // oxlint-disable-next-line no-console
        (e) => console.error(`Could not load urn`, e),
      );
    },
    // oxlint-disable-next-line react/preserve-manual-memoization
    [urn, viewer],
  );

  const resize = useDebouncedCallback(() => viewer.current?.resize(), 250);

  return (
    <>
      <div
        ref={(ref: HTMLDivElement | null) => {
          if (!ref) return undefined;

          const getAccessToken = props.getAccessToken ?? getAccessTokenCb;
          initializeViewerRuntime({
            ...props,
            getAccessToken,
          })
            .then(async () => {
              if (viewer.current || !mounted.current) return;

              viewer.current = new Autodesk.Viewing.GuiViewer3D(ref, {
                extensions: [...(props.extensions ?? []), ...defaultExtensions],
              });

              const loadExtensions = async () => {
                if (isNil(viewer.current)) return;

                await Promise.allSettled([
                  loadEvolveExtension(viewer.current, 'SelectionToolsExtension', {}),
                  loadEvolveExtension(viewer.current, 'EvolveDimensioningExtension', {
                    viewerStore: contexts['apsViewer'],
                    elementTagsStore: contexts['elementTags'],
                    dimensioningStore: contexts['dimensioning'],
                    mountPortal,
                  }),
                  loadEvolveExtension(viewer.current, 'APSViewerStoreExtension', {
                    store: contexts['apsViewer'],
                  }),
                  loadEvolveExtension(viewer.current, 'SpoolsExtension', {
                    urn,
                    autoSelectSpool,
                    showSpoolMenu,
                    spoolStore: contexts['spools'],
                    mountPortal,
                  }),
                ]);
              };

              viewer.current.addEventListener(Autodesk.Viewing.EXTENSION_LOADED_EVENT, hideExtensions);
              viewer.current.addEventListener(Autodesk.Viewing.GEOMETRY_LOADED_EVENT, loadExtensions);

              // Load optional, context-based extensions if the context is available.

              await viewer.current.start();
              await syncState({});
            })
            // oxlint-disable-next-line no-console
            .catch(console.error);

          const resizeObserver = new ResizeObserver(resize);
          resizeObserver.observe(ref);
          return () => {
            resizeObserver.disconnect();
          };
        }}
      />
      {portals}
    </>
  );
};

export const ForgeViewerFromModel = ({ urn, ...props }: ForgeViewerProps) => (
  <DeferredForgeViewer url={urn}>
    {(deferredUrn) => <ForgeViewerWrapped {...props} urn={deferredUrn} />}
  </DeferredForgeViewer>
);
